/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
    DataGridPro,
    GridActionsCellItem,
    GridRowParams,
    GridRow,
    GridRowProps,
    GridFooterContainerProps,
    GridFooterContainer,
    GridFooter,
    GridValueFormatterParams,
    GridValueGetterParams,
    GridAlignment,
    GridColDef,
    GridActionsColDef,
    GridRenderCellParams,
    useGridApiContext,
    GridCsvExportOptions,
    GridInputRowSelectionModel,
    GridCellParams,
    GridEventListener,
    GridValidRowModel,
    LicenseInfo,
    GridRowSelectionModel,
    GridRowId,
    GridRowModel,
    GridToolbarContainer,
    GridToolbarQuickFilter,
    GridRowOrderChangeParams,
} from '@mui/x-data-grid-pro';
import { Checkbox, Icon, IconButton, useTheme } from '@mui/material';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomTableHelper from './helper/customTableHelper';
import CustomTooltip from '../tooltip/Tooltip';
import ArrayHelper from '../../../services/helper/arrayHelper';
import DataGridHelper from '../../../services/helper/dataGridHelper';
import { DATE_FORMAT_DEFAULT_NO_TIME, DATE_FORMAT_NO_SECONDS } from '../../../appConstants';
import { addea, addkg, booleanToYesNo } from '../../../services/helper/functionHelper';
import FloatingActionButton from '../input/FloatingActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import theme from '../../../material';

LicenseInfo.setLicenseKey(
    MUIXLICENCE,
);

export interface ICustomTableProps<T extends { [key : string] : any }> {
    id ?: string;
    rows : Array<T>;
    columns : Array<ICustomTableColumn>;
    enableQuickFilter ?: boolean;
    enableFiltering ?: boolean;
    enableSorting ?: boolean;
    enableAdding ?: boolean;
    enableEditing ?: ((row : T) => boolean | undefined) | boolean;
    enableDeleting ?: ((row : T) => boolean | undefined) | boolean;
    enableDetails ?: ((row : T) => boolean | undefined) | boolean;
    enableRefresh ?: boolean;
    enableSelection ?: boolean;
    enableCSVExport ?: boolean;
    enableRowReordering ?: boolean;
    csvExportPathname ?: string;
    enableClearFilterButton ?: boolean;
    disableRefreshButton ?: boolean;
    enableHorizontalVirtualize ?: boolean;
    enableDetailAccordion ?: ((row : T) => boolean | undefined) | boolean;
    detailAccordionHeight ?: number | 'auto';
    disableDeleteButton ?: ((row : T) => boolean | undefined) | boolean;
    disableDetailButton ?: ((row : T) => boolean | undefined) | boolean;
    disableEditButton ?: ((row : T) => boolean | undefined) | boolean;
    disableRowSelect ?: (row : T | GridValidRowModel) => boolean;
    disableAddButton ?: boolean;
    isActive ?: ((row : T) => boolean | undefined) | boolean;
    warning ?: ((row : T) => boolean | undefined) | boolean;
    rowColor ?: ((row : T) => string | undefined) | string;
    textColor ?: ((row : T) => string | undefined) | string;
    detailIcon ?: string | React.ReactElement<unknown>;
    detailsColor ?: string;
    deleteIcon ?: string | React.ReactElement<unknown>;
    deleteColor ?: string;
    editIcon ?: string | React.ReactElement<unknown>;
    addIcon ?: string | React.ReactElement<unknown>;
    editColor ?: string;
    addTooltipRightValidation ?: boolean;
    editTooltipRightValidation ?: boolean;
    deleteTooltipRightValidation ?: boolean;
    detailTooltip ?: ((row : T) => string | undefined) | string;
    deleteTooltip ?: ((row : T) => string | undefined) | string;
    editTooltip ?: ((row : T) => string | undefined) | string;
    addTooltip ?: string;
    addTooltipOnClick ?: () => void;
    editTooltipOnClick ?: () => void;
    deleteTooltipOnClick ?: () => void;
    addFunction ?: () => void;
    editFunction ?: (row : T) => void;
    deleteFunction ?: (row : T) => void;
    detailFunction ?: (row : T) => void;
    refreshFunction ?: () => void;
    onRowOrderChange ?: (params : GridRowOrderChangeParams) => void;
    detailAccordionComponent ?: (row : T) => React.ReactElement<unknown>;
    initialSortOrder ?: Array<{columnName : string; direction : 'asc' | 'desc'}>;
    enablePagination ?: boolean;
    enableTotalRow ?: boolean;
    pageSizes ?: Array<number>;
    fitWidthToPage ?: boolean;
    autoRowHeight ?: boolean;
    rowHeight ?: number;
    disableMultiSelect ?: boolean;
    hide ?: boolean;
    pageHeight ?: number;
    onSelectChange ?: (ids : GridRowSelectionModel) => void;
    selectedRows ?: GridInputRowSelectionModel;
    filterType ?: 'Textfield' | 'MultiCheckbox';
    filterTypeColumn ?: Array<{ column : string; data : Array<string> }>;
    isCellEditable ?: ((params : GridCellParams<T>) => boolean) | undefined;
    onCellEditStop ?: GridEventListener<'cellEditStop'>;
    filteredRows ?: (rows : Array<T>) => void;
    headerColor ?: 'light' | 'main';
    rowSelectTooltipMessage ?: ((rowId : number) => string) | string;
    useGuidAsParamsIdType ?: boolean;
    slimMode ?: boolean;
    hideFooter ?: boolean;
}

export interface ICustomTableColumn {
    title : string;
    field : string;
    type ?: 'string' | 'number' | 'date' | 'boolean' | 'singleSelect' | 'actions' | string;
    width ?: number;
    flex ?: number;
    titleContainerComponent ?: (value : unknown) => React.ReactElement<unknown> | undefined;
    containerComponent ?: (row : unknown, value : unknown) => JSX.Element | React.ReactElement<unknown> | string | undefined;
    formatFunction ?: (value : any, row ?: any) => any;
    redFormatFunction ?: (value : any, row ?: any) => any;
    sortFunction ?: (value : any, value2 : any) => any;
    cellClassName ?: (row : unknown) => string;
    calculateTotal ?: boolean;
    calculateRedTotal ?: boolean;
    isNumberString ?: boolean;
    style ?: (value : unknown) => React.CSSProperties | undefined;
    freezeUpTo ?: boolean;
    enableFiltering ?: boolean;
    disableExport ?: boolean;
    enableSorting ?: boolean;
    dontFormatFilter ?: boolean;
    editable ?: boolean;
}

const defaultColumnWidth = 150;
const GridRowContext = React.createContext<string>('');

/**
 * @template T - Generic type for table rows, representing the structure of each row's data.
 * @param {string} id - Unique identifier for the table. Optional.
 * @param {Array<T>} rows - Array of row data objects to be displayed in the table. Required.
 * @param {Array<ICustomTableColumn>} columns - Definitions of the columns to be displayed in the table, including headers and formatting. Required.
 * @param {boolean} enableFiltering - If true, enables a filtering feature on the table. Optional.
 * @param {boolean} enableSorting - If true, allows sorting on the table columns. Optional.
 * @param {boolean} enableAdding - If true, allows adding new rows to the table. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} enableEditing - Enables editing of rows; can be a boolean or a function returning a boolean to conditionally allow editing based on the row. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} enableDeleting - Enables deleting of rows; can be a boolean or a function returning a boolean to conditionally allow deleting based on the row. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} enableDetails - Enables a detail view for rows; can be a boolean or a function returning a boolean to conditionally show details based on the row. Optional.
 * @param {boolean} enableRefresh - If true, enables a refresh feature that can reload the table data. Optional.
 * @param {boolean} enableSelection - If true, allows rows to be selected. Optional.
 * @param {boolean} enableCSVExport - If true, enables the export of table data to a CSV file. Optional.
 * @param {boolean} enableRowReordering - If true, enables the rearranging of rows. Optional.
 * @param {string} csvExportPathname - Specifies the path for the exported CSV file. Optional.
 * @param {boolean} enableClearFilterButton - If true, adds a button to clear all active filters. Optional.
 * @param {boolean} disableRefreshButton - If true, the refresh button is disabled. Optional.
 * @param {boolean} enableHorizontalVirtualize - If true, enables horizontal virtualization of rows for performance optimization. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} enableDetailAccordion - Enables an accordion-style detail view; can be a boolean or a function returning a boolean based on the row. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} disableDeleteButton - Conditionally disables the delete button for specific rows. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} disableDetailButton - Conditionally disables the detail button for specific rows. Optional.
 * @param {(boolean | ((row: T) => boolean | undefined))} disableEditButton - Conditionally disables the edit button for specific rows. Optional.
 * @param {(T | GridValidRowModel) => boolean} disableRowSelect - Function to conditionally disable row selection. Optional.
 * @param {boolean} disableAddButton - If true, disables the add new row button. Optional.
 * @param {(row: T) => boolean | undefined} isActive - Function to determine if a row is considered "active" based on its data. Optional.
 * @param {(row: T) => boolean | undefined} warning - Function to determine if a row should display a warning based on its data. Optional.
 * @param {(row: T) => string | undefined} rowColor - Function to determine the background color of a row based on its data. Optional.
 * @param {(row: T) => string | undefined} textColor - Function to determine the text color of a row based on its data. Optional.
 * @param {string | React.ReactElement<unknown>} detailIcon - Icon for the detail view toggle. Optional.
 * @param {string} detailsColor - Color for the detail view elements. Optional.
 * @param {string | React.ReactElement<unknown>} deleteIcon - Icon for the delete button. Optional.
 * @param {string} deleteColor - Color for the delete button icon. Optional.
 * @param {string | React.ReactElement<unknown>} editIcon - Icon for the edit button. Optional.
 * @param {string | React.ReactElement<unknown>} addIcon - Icon for the add new row button. Optional.
 * @param {string} editColor - Color for the edit button icon. Optional.
 * @param {boolean} addTooltipRightValidation - If true, displays tooltips for the add button to the right. Optional.
 * @param {boolean} editTooltipRightValidation - If true, displays tooltips for the edit button to the right. Optional.
 * @param {boolean} deleteTooltipRightValidation - If true, displays tooltips for the delete button to the right. Optional.
 * @param {(row: T) => string | undefined} detailTooltip - Function to provide tooltips for the detail button based on the row. Optional.
 * @param {(row: T) => string | undefined} deleteTooltip - Function to provide tooltips for the delete button based on the row. Optional.
 * @param {(row: T) => string | undefined} editTooltip - Function to provide tooltips for the edit button based on the row. Optional.
 * @param {string} addTooltip - Tooltip for the add new row button. Optional.
 * @param {() => void} addTooltipOnClick - Callback function when the add tooltip is clicked. Optional.
 * @param {() => void} editTooltipOnClick - Callback function when the edit tooltip is clicked. Optional.
 * @param {() => void} deleteTooltipOnClick - Callback function when the delete tooltip is clicked. Optional.
 * @param {() => void} addFunction - Callback function to handle adding a new row. Optional.
 * @param {(row: T) => void} editFunction - Callback function to handle editing a specific row. Optional.
 * @param {(row: T) => void} deleteFunction - Callback function to handle deleting a specific row. Optional.
 * @param {(row: T) => void} detailFunction - Callback function to handle showing details of a specific row. Optional.
 * @param {() => void} refreshFunction - Callback function to refresh the table data. Optional.
 * @param {(GridRowOrderChangeParams) => void} onRowOrderChange - Callback function when a row's order is rearranged. Optional.
 * @param {(row: T) => React.ReactElement<unknown>} detailAccordionComponent - Component to render as the content of the detail accordion for a row. Optional.
 * @param {Array<{columnName: string; direction: 'asc' | 'desc'}>} initialSortOrder - Defines the initial sort order of the table. Optional.
 * @param {boolean} enablePagination - If true, enables pagination controls for the table. Optional.
 * @param {boolean} enableTotalRow - If true, displays a total row at the bottom of the table. Optional.
 * @param {Array<number>} pageSizes - Array of numbers defining the available page sizes for pagination. Optional.
 * @param {boolean} fitWidthToPage - If true, adjusts the table width to fit the page. Optional.
 * @param {boolean} autoRowHeight - If true, automatically adjusts the height of rows based on content. Optional.
 * @param {number} rowHeight - Set row height to provided value. Optional.
 * @param {boolean} disableMultiSelect - If true, disables the ability to select multiple rows. Optional.
 * @param {boolean} hide - If true, hides the entire table. Optional.
 * @param {number} pageHeight - Specifies the height of the table paging area. Optional.
 * @param {(ids: GridRowSelectionModel) => void} onSelectChange - Callback function triggered when the selection of rows changes. Optional.
 * @param {GridInputRowSelectionModel} selectedRows - Model for managing the selected rows state. Optional.
 * @param {'Textfield' | 'MultiCheckbox'} filterType - Specifies the type of filter to use on the table. Optional.
 * @param {Array<{column: string; data: Array<string>}>} filterTypeColumn - Specifies filtering options for specific columns. Optional.
 * @param {((params: GridCellParams<T>) => boolean) | undefined} isCellEditable - Function to determine if a cell is editable based on its parameters. Optional.
 * @param {GridEventListener<'cellEditStop'>} onCellEditStop - Event listener for when cell editing is stopped. Optional.
 * @param {(rows: Array<T>) => void} filteredRows - Function to process and update the table with filtered rows. Optional.
 * @param {'light' | 'main'} headerColor - Specifies the color theme for the table headers. Optional.
 * @param {((rowId: number) => string) | string} rowSelectTooltipMessage - Tooltip message or a function returning a tooltip message when row selection is disabled. Optional.
 * @param {number | 'auto' | undefined} detailAccordionHeight - Specify detail accordion view height. Optional.
 * @param {boolean} useGuidAsParamsIdType - If true, uses the guid over the id for uniquely identifying and displaying each row.
 * @param {boolean} hideFooter - If true, hides table footer.
 * @param {boolean} slimMode - If true, reduces row and footer height to 34 pixels.
 * 
 * @return {React.ReactElement} The React element for the Custom Table component.
 */
const CustomTable = <T extends { [key : string] : any }>(props : ICustomTableProps<T>) : JSX.Element => {
    const currentTheme = useTheme();

    const { 
        rows, 
        columns, 
        headerColor, 
        enableDetails, 
        enableEditing, 
        enableDeleting, 
        enableFiltering, 
        enableSorting, 
        enableSelection, 
        enableAdding,
        enableDetailAccordion, 
        enableRefresh, 
        enableCSVExport,
        enableRowReordering,
        editIcon, 
        detailIcon, 
        deleteIcon, 
        addIcon,
        editTooltip, 
        detailTooltip, 
        deleteTooltip, 
        addTooltip, 
        addTooltipRightValidation, 
        editTooltipRightValidation, 
        deleteTooltipRightValidation,
        addTooltipOnClick, 
        editTooltipOnClick, 
        deleteTooltipOnClick,
        editFunction, 
        deleteFunction, 
        detailFunction, 
        addFunction, 
        refreshFunction,
        onRowOrderChange,
        disableEditButton, 
        disableDeleteButton, 
        disableDetailButton, 
        disableAddButton,
        isActive, 
        warning, 
        rowColor, 
        textColor, 
        autoRowHeight,
        rowHeight,
        isCellEditable, 
        onCellEditStop, 
        rowSelectTooltipMessage,
        detailAccordionHeight,
        detailsColor,
        deleteColor,
        editColor,
        useGuidAsParamsIdType,
        hideFooter,
        slimMode } = props;

    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = React.useState<Array<GridRowId>>([]);

    const handleDetailPanelExpandedRowIdsChange = React.useCallback(
        (newIds : Array<GridRowId>) => {
            setDetailPanelExpandedRowIds(newIds);
        }, []);

    const renderDetailAccordionComponent = (params : GridRowParams<GridValidRowModel>) : React.ReactElement => {
        const row = rows.find(x => x === params.row);

        if (row && props.detailAccordionComponent) {
            return props.detailAccordionComponent(row);
        } else {
            console.error('No detail accordion component supplied!');
            return <div/>;
        }
    };

    // Custom row component that extends DataGridRow
    const CustomRow = (rowProps : GridRowProps) : JSX.Element => {
        let paramsIdType : 'id' | 'guid';
        
        // Fallback to guid if multiple rows have Ids = 0
        if (!useGuidAsParamsIdType && 'rowId' in rowProps && typeof rowProps.rowId === 'number' && rowProps.rowId > 0) {
            paramsIdType = 'id';
        } else {
            paramsIdType = 'guid';
        }

        const row = rows.find(x => paramsIdType === 'id' ? x.id === Number(rowProps.rowId) : x.guid === rowProps.rowId.toString());
        if (row) {
            const index = rowProps.index % 2;
            const rowIsActive = typeof isActive === 'boolean' ? isActive : (isActive ? isActive(row) : true);
            const rowWarning = typeof warning === 'boolean' ? warning : (warning ? warning(row) : false);
            const colorForRow = typeof rowColor === 'string' ? rowColor : (rowColor ? rowColor(row) : '');
            const textColorForRow = typeof textColor === 'string' ? textColor : (textColor ? textColor(row) : '');
            const toolTipMessage = typeof rowSelectTooltipMessage === 'string' ? rowSelectTooltipMessage : (rowSelectTooltipMessage ? rowSelectTooltipMessage(row.id) : '');

            const rowBackgroundColor = (index % 2 !== 0) ?
                (!rowIsActive ? '#fad1d0' :
                    (rowWarning ? '#f8cca3' :
                        (colorForRow ? colorForRow : '#EAEAEA')))
                :
                (!rowIsActive ? '#ffc6c4' :
                    (rowWarning ? '#fdb482' :
                        (colorForRow ? colorForRow : '#FFFFFF')));

            const rowTextColor = textColorForRow ? textColorForRow : '';

            return (
                <GridRowContext.Provider value={toolTipMessage}>
                    <GridRow
                        {...rowProps}
                        className={`${rowBackgroundColor} ${rowTextColor} aic jcc`}
                        style={{ 
                            backgroundColor: rowBackgroundColor,   
                            border: `1px solid ${rowBackgroundColor}`,
                            height: rowHeight ? rowHeight : (slimMode ? 34 : 52),
                        }}
                    />
                </GridRowContext.Provider>

            );
        }
        return <GridRow
            {...rowProps}
        />;
    };

    // eslint-disable-next-line no-console
    const onRefreshButtonClick = () : void => props.refreshFunction ? props.refreshFunction() : console.error('No refresh function supplied!');

    const CustomFooter = (footerProps : GridFooterContainerProps) : JSX.Element => {
        const apiRef = useGridApiContext();

        const handleExport = (options : GridCsvExportOptions) : void => apiRef.current.exportDataAsCsv(options);

        const path = (enableCSVExport) ? useLocation().pathname : '';
        const csvFileName = props.csvExportPathname
            ? `${props.csvExportPathname}-${moment(moment.now()).format(DATE_FORMAT_NO_SECONDS)}`
            : `CSV${path ? path.replace('/', '-') : ''}-${moment(moment.now()).format(DATE_FORMAT_NO_SECONDS)}`;

        return (
            <GridFooterContainer
                {...footerProps}
                style={{
                    backgroundColor: '#eaeaea',
                    height: (slimMode ? 34 : 52),
                    minHeight: (slimMode ? 34 : 52),
                }}
            >
                <div className={'fdr flx1 btbw1 bocg'}>
                    <GridFooter 
                        style={{ color: '#424242' }} 
                        className='flx1' />
                    <div className='w20'/>
                    <>
                        {enableCSVExport &&
                            <div className='p3'>
                                <FloatingActionButton
                                    color={'secondary'}
                                    className={`ml10 mr10 mt5 mb5 ${(slimMode ? 'h30 w30' : '')}`}
                                    size={'medium'}
                                    onClick={() : void => handleExport({ fileName: 'csvFileName' })}
                                >
                                    <FontAwesomeIcon className={'ml10 mr10 mb1 cpd'} size={slimMode ? '1x' : '2x'} icon={faFileCsv}/>
                                </FloatingActionButton>
                            </div>
                        }
                    </>
                    <div className='w20'/>
                    {enableAdding &&
                        <CustomTooltip
                            title={addTooltip ? addTooltip : 'Add'}
                            rightValidation={addTooltipRightValidation}
                            onClick={addTooltipOnClick}>
                            <div className='p3'>
                                <FloatingActionButton
                                    color={'secondary'}
                                    className={`ml10 mr10 mt5 mb5 p20 ${(slimMode ? 'h30 w30' : '')}`}
                                    onClick={addFunction}
                                    size={'medium'}
                                    disabled={disableAddButton}>
                                    {addIcon ? <Icon fontSize={'large'} className={'cpd'}>{addIcon}</Icon> : undefined}
                                </FloatingActionButton>
                            </div>
                        </CustomTooltip>
                    }
                </div>
            </GridFooterContainer>
        );
    };

    const getField = (field : string, title : string) : string => {
        /* This handles columns that have special actions/renders and use id as the field identifier,
           since this is now a unique identifier we modify them like this to pass the id instead of value and have a unique field */
        return (field === 'id' && title.toLowerCase() !== 'id') ? title.toLowerCase() : field;
    };

    const renderCell = (param : GridRenderCellParams<T>, field : string, title : string, containerComponent ?: (value : unknown, row ?: unknown) => JSX.Element | React.ReactElement<unknown> | string | undefined) : string | JSX.Element | undefined => {
        /* This handles columns that have special actions/renders and use id as the field identifier,
           since this is now a unique identifier we modify them like this to pass the id instead of value and have a unique field */
        if (field === 'id' && title.toLowerCase() !== 'id') {
            return containerComponent ? containerComponent(param.row, param.id) : undefined;
        }
        return containerComponent ? containerComponent(param.row, param.value) : undefined;
    };

    const getNestedObject = (nestedObj : { [key : string] : unknown } | undefined | unknown, pathArr : Array<string>) : unknown => pathArr.reduce(
        (obj : { [key : string] : unknown } | undefined | unknown, key : string) => (obj && obj[key as keyof object] !== 'undefined') ? obj[key as keyof object] : undefined, nestedObj
    );

    const getFieldValue = (row : T | undefined, field : string) : any => getNestedObject(row, field.split('.'));

    const valueFormatter = (param : GridValueFormatterParams<T>, field : string, title : string, formatFunction ?: (value : unknown, row ?: unknown) => unknown) : unknown => {
        let paramsIdType : 'id' | 'guid';
        
        // Fallback to guid if multiple rows have Ids = 0
        if (!useGuidAsParamsIdType && 'id' in param && typeof param.id === 'number' && param.id > 0) {
            paramsIdType = 'id';
        } else {
            paramsIdType = 'guid';
        }

        const row = rows.find(x => paramsIdType === 'id' ? x.id === Number(param.id) : x.guid === param.id?.toString());

        /* This handles columns that have special actions/renders and use id as the field identifier,
           since this is now a unique identifier we modify them like this to pass the id instead of value and have a unique field */
        if (field === 'id' && title.toLowerCase() !== 'id') {
            return formatFunction && (formatFunction.toString() !== booleanToYesNo.toString()) ? formatFunction(param.id, row) : param.id;
        }
        return formatFunction && (formatFunction.toString() !== booleanToYesNo.toString()) ? formatFunction(getFieldValue(row, field), row) : getFieldValue(row, field);
    };

    const valueGetter = (param : GridValueGetterParams<T>, field : string, title : string, formatFunction ?: (value : unknown, row ?: unknown) => unknown, type ?: 'string' | 'number' | 'date' | 'dateTime' | 'boolean' | 'singleSelect' | 'actions' | string) : unknown => {
        /* This handles columns that have special actions/renders and use id as the field identifier,
           since this is now a unique identifier we modify them like this to pass the id instead of value and have a unique field */
        if (type === 'dateTime' || type === 'date') {
            if (param.value && formatFunction && (formatFunction.toString() !== booleanToYesNo.toString())) {
                const formattedDate = formatFunction(getFieldValue(param.row, field), param.row) as string;

                return formattedDate;
            } else {
                return undefined;
            }
        }
        if (field === 'id' && title.toLowerCase() !== 'id') {
            return formatFunction && (formatFunction.toString() !== booleanToYesNo.toString()) ? formatFunction(param.id, param.row) : param.id;
        }
        return formatFunction && (formatFunction.toString() !== booleanToYesNo.toString()) ? formatFunction(getFieldValue(param.row, field), param.row) : getFieldValue(param.row, field);
    };

    const formatColumns = React.useMemo(() => {
        if (columns.length > 0) {
            let result : Array<GridColDef<T>> = [...columns.map((x) => {
                let type = x.type;
                let dontFormatFilter = x.dontFormatFilter;
                if (!x.type || (x.type === 'string')) {
                    if (x.formatFunction?.toString() === addea.toString() || x.formatFunction?.toString() === addkg.toString()) {
                        type = 'number';
                        dontFormatFilter = true;
                    }

                    if (x.formatFunction?.toString() === booleanToYesNo.toString()) {
                        type = 'boolean';
                    }
                }

                const column : GridColDef = {
                    field: getField(x.field, x.title),
                    headerName: x.title,
                    type: (type === 'date' || type === 'dateTime') ? 'string' : type,
                    editable: x.editable,
                    disableExport: x.disableExport,
                    headerAlign: 'left' as GridAlignment,
                    align: 'left' as GridAlignment,
                    width: x.width ?? defaultColumnWidth,
                    flex: x.flex ?? (props.fitWidthToPage ? 1 : undefined),
                    cellClassName: (params : GridCellParams) => {
                        let paramsIdType : 'id' | 'guid';
                        
                        // Fallback to guid if multiple rows have Ids = 0
                        if (!useGuidAsParamsIdType && 'id' in params && typeof params.id === 'number' && params.id > 0) {
                            paramsIdType = 'id';
                        } else {
                            paramsIdType = 'guid';
                        }
    
                        const row = rows.find(x => paramsIdType === 'id' ? x.id === Number(params.id) : x.guid === params.id.toString());
                        return typeof x.cellClassName === 'string'
                            ? x.cellClassName
                            : (x.cellClassName ? x.cellClassName(row) : '');
                    },
                };

                if (type === 'date') {
                    column.sortComparator = CustomTableHelper.CustomGridDateSorter;
                    column.filterOperators = CustomTableHelper.CustomGridDateOperator
                        .map((operator : any) => ({
                            ...operator,
                            InputComponent: ((operator.value !== 'isEmpty') && (operator.value !== 'isNotEmpty')) ? (textFieldProps : any) : JSX.Element => {
                                const selectedValue = textFieldProps.item.value ? moment(textFieldProps.item.value, DATE_FORMAT_DEFAULT_NO_TIME) : undefined;

                                return <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        className={'w180'}
                                        label={'Value'}
                                        format={DATE_FORMAT_DEFAULT_NO_TIME}
                                        value={selectedValue}
                                        onChange={(value : moment.Moment | null) : void => {
                                            textFieldProps.apiRef.current.upsertFilterItem({
                                                field: column.field,
                                                operator: textFieldProps.item.operator,
                                                value: moment(value, DATE_FORMAT_DEFAULT_NO_TIME),
                                                id: textFieldProps.item.id,
                                            });
                                        }}
                                        slotProps={{
                                            textField: { variant: 'standard' },
                                            actionBar: {
                                                actions: ['clear'],
                                            },
                                        }}
                                    />
                                </LocalizationProvider>;
                            } : undefined,
                        }));
                }

                if (x.sortFunction) {
                    column.sortComparator = x.sortFunction;
                }

                if (x.containerComponent) {
                    column.renderCell = (param : any) : string | JSX.Element | undefined => renderCell(param, x.field, x.title, x.containerComponent);
                }

                if (!dontFormatFilter) {
                    column.valueGetter = (param : any) : unknown => valueGetter(param, x.field, x.title, x.formatFunction, type);
                } else {
                    column.valueFormatter = (param : any) : unknown => valueFormatter(param, x.field, x.title, x.formatFunction);
                }
                return column;
            })];

            const actions : GridActionsColDef = {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                headerAlign: 'left' as GridAlignment,
                align: 'left' as GridAlignment,
                disableExport: true,
                width: (enableDetails ? 40 : 0) + (enableEditing ? 40 : 0) + (enableDeleting ? 40 : 0) + (enableRefresh && !enableDetails && !enableEditing && !enableDeleting && !enableDetailAccordion ? 40 : 0),
                cellClassName: 'actions',
                getActions: (params : GridRowParams) => {
                    let paramsIdType : 'id' | 'guid';
                    
                    // Fallback to guid if multiple rows have Ids = 0
                    if (!useGuidAsParamsIdType && 'id' in params && typeof params.id === 'number' && params.id > 0) {
                        paramsIdType = 'id';
                    } else {
                        paramsIdType = 'guid';
                    }

                    const row = rows.find(x => paramsIdType === 'id' ? x.id === Number(params.id) : x.guid === params.id.toString());
                    const acts = [];
                    if (row) {
                        if (enableEditing && (typeof enableEditing === 'boolean' || enableEditing(row))) {
                            const isEditDisabled = disableEditButton && (disableEditButton === true || disableEditButton(row));
                            const iconColor = !editColor ? (!isEditDisabled ? 'cpd' : '') : editColor;
                            acts.push(
                                <CustomTooltip
                                    title={editTooltip ? (typeof editTooltip === 'string' ? editTooltip : (editTooltip(row) ?? 'Edit')) : 'Edit'}
                                    rightValidation={editTooltipRightValidation}
                                    onClick={editTooltipOnClick}>
                                    <GridActionsCellItem
                                        icon={<Icon className={`${iconColor}`}>{editIcon ? editIcon : 'edit'}</Icon>}
                                        label='Edit'
                                        disabled={isEditDisabled}
                                        onClick={() : void | null => editFunction ? editFunction(row) : null}
                                        showInMenu={false}
                                    />
                                </CustomTooltip>
                            );
                        }
                        if (enableDeleting && (typeof enableDeleting === 'boolean' || enableDeleting(row))) {
                            const isDeleteDisabled = disableDeleteButton && (disableDeleteButton === true || disableDeleteButton(row));
                            const iconColor = !deleteColor ? (!isDeleteDisabled ? 'cr' : '') : deleteColor;
                            acts.push(
                                <CustomTooltip
                                    title={deleteTooltip ? (typeof deleteTooltip === 'string' ? deleteTooltip : (deleteTooltip(row) ?? 'Delete')) : 'Delete'}
                                    rightValidation={deleteTooltipRightValidation}
                                    onClick={deleteTooltipOnClick}>
                                    <GridActionsCellItem
                                        icon={<Icon className={`${iconColor}`}>{deleteIcon ? deleteIcon : 'delete'}</Icon>}
                                        label='Delete'
                                        disabled={isDeleteDisabled}
                                        onClick={() : void | null => deleteFunction ? deleteFunction(row) : null}
                                        showInMenu={false}
                                    />
                                </CustomTooltip>
                            );
                        }
                        if (enableDetails && (typeof enableDetails === 'boolean' || enableDetails(row))) {
                            const isDetailDisabled = disableDetailButton && (disableDetailButton === true || disableDetailButton(row));
                            const iconColor = !detailsColor ? (!isDetailDisabled ? 'cpd' : '') : detailsColor;
                            acts.push(
                                <CustomTooltip title={detailTooltip ? (typeof detailTooltip === 'string' ? detailTooltip : (detailTooltip(row) ?? 'Detail')) : 'Detail'}>
                                    <GridActionsCellItem
                                        icon={<Icon className={`${iconColor}`}>{detailIcon ? detailIcon : 'list'}</Icon>}
                                        label='Details'
                                        disabled={isDetailDisabled}
                                        onClick={() : void | null => detailFunction ? detailFunction(row) : null}
                                        showInMenu={false}
                                    />
                                </CustomTooltip>
                            );
                        }
                    }
                    return acts;
                },
            };

            if (enableRefresh && refreshFunction) {
                actions.renderHeader = () : JSX.Element => <CustomTooltip title={'Refresh Data'}>
                    <IconButton disabled={props.disableRefreshButton} className={'w48 mnw48'} onClick={onRefreshButtonClick}>
                        <Icon className={'cw'}>refresh</Icon>
                    </IconButton></CustomTooltip>;
            }
            if (enableDetails || enableEditing || enableDeleting || enableRefresh) {
                result = ArrayHelper.addElement(result, actions, 'start');
            }
            if (!props.enableSorting) {
                result.forEach(x => x.sortable = false);
            }
            return result;
        }
        return [];
    }, [columns, rows]);

    const formatRows = React.useMemo(() => {
        if (columns.length > 0 && rows.length > 0) {
            const result = [...rows];
            return result;
        }
        return [];
    }, [rows, columns]);

    const getHeaderColor = React.useMemo<{ text : string; background : string }>(() => {
        if (headerColor && headerColor === 'light') {
            return {
                text: '#0d703f',
                background: '#B9C9C0',
            };
        } else {
            return {
                text: '#FFFFFF',
                background: '#0d703f',
            };
        }
    }, [headerColor]);

    const CustomCheckbox = ({...props }) : React.ReactElement => {
        const rowToolTipMessage = React.useContext(GridRowContext);

        return (
            <CustomTooltip title={rowToolTipMessage}>
                <span>
                    <Checkbox {...props} disabled={props.disabled}/>
                </span>
            </CustomTooltip>
        );
    };

    const CustomGridToolbar = () : React.ReactElement => {
        return (
            <GridToolbarContainer className={'jcfe br25'}>
                <GridToolbarQuickFilter
                    sx={{
                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: currentTheme.palette.primary.light,
                        },
                        '& .MuiFormHelperText-root': {
                            color: currentTheme.palette.primary.main,
                        },
                    }}
                />
            </GridToolbarContainer>
        );
    };

    return <DataGridPro
        rowReordering={enableRowReordering}
        onRowOrderChange={onRowOrderChange}
        disableColumnFilter={!enableFiltering}
        disableChildrenFiltering={!enableFiltering}
        disableMultipleColumnsSorting={!enableSorting}
        disableChildrenSorting={!enableSorting}
        checkboxSelection={enableSelection}
        disableRowSelectionOnClick
        isCellEditable={isCellEditable}
        style={{ backgroundColor: '#FFFFFF' }}
        sx={{
            border: 'none',
            '.MuiDataGrid-columnHeaders': {
                'color': `${getHeaderColor.text} !important`,
                'backgroundColor': `${getHeaderColor.background} !important`,
            },
            '.MuiDataGrid-pinnedColumnHeaders': {
                'color': `${getHeaderColor.text} !important`,
                'backgroundColor': `${getHeaderColor.background} !important`,
            },
        }}
        slots={{
            toolbar: props.enableQuickFilter 
                ? CustomGridToolbar 
                : null,
            row: CustomRow,
            baseCheckbox: CustomCheckbox,
            footer: CustomFooter,
        }}
        columnBuffer={props.enableHorizontalVirtualize ? 3 : 100}
        hideFooter={hideFooter}
        className={`hfill ${formatRows.length > 0 ? '' : 'mnh200'}`}
        onCellEditStop={onCellEditStop}
        columnHeaderHeight={34}
        getRowHeight={() : 'auto' | number | undefined => autoRowHeight
            ? 'auto'
            : rowHeight
                ? rowHeight
                : (slimMode ? 34 : undefined)}
        columns={formatColumns}
        onRowSelectionModelChange={props.rows.length > 0 ? props.onSelectChange : undefined}
        rowSelectionModel={props.selectedRows}
        rows={formatRows}
        initialState={{
            sorting: {
                sortModel: props.initialSortOrder?.map((x) => {
                    return {
                        field: x.columnName.split('_')[0],
                        sort: x.direction,
                    };
                }),
            },
        }}
        disableMultipleRowSelection={props.disableMultiSelect}
        isRowSelectable={(params) : boolean => (enableSelection && props.disableRowSelect && !props.disableRowSelect(params.row)) ?? false}
        getRowId={(row : GridRowModel<Record<string, unknown>>) : string | number => DataGridHelper.getRowId(row, props.useGuidAsParamsIdType)}
        getDetailPanelContent={enableDetailAccordion ? renderDetailAccordionComponent : undefined}
        getDetailPanelHeight={() : number | 'auto' => detailAccordionHeight ?? 300}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
    />;
};

export default CustomTable;
